import { useEffect, useMemo, useRef, useState } from "react";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type {
	CampaignCardView,
	CampaignHourPoint,
} from "../../../shared/types/campaign-card.type";
import { fmtMoney, fmtNum } from "../../../shared/utils/campaign-metrics-format";
import {
	HOURLY_METRICS_STORAGE_KEY,
	readHourlyMetricsSelection,
	saveHourlyMetricsSelection,
} from "../utils/hourly-metrics-storage";

type Props = {
	campaign: CampaignCardView;
};

type HourlyMetricKey = keyof Omit<CampaignHourPoint, "hour">;

const HOURLY_METRICS: {
	key: HourlyMetricKey;
	label: string;
	color: string;
	axis: "left" | "right";
}[] = [
	{ key: "impressions", label: "Impressions", color: "#3db8d4", axis: "right" },
	{ key: "cost", label: "Cost $", color: "#e88a55", axis: "left" },
	{ key: "cpa_fact", label: "CPA fact $", color: "#e8c055", axis: "left" },
	{ key: "installs", label: "Installs", color: "#5abf8a", axis: "left" },
	{ key: "registrations", label: "Registrations", color: "#a88ae8", axis: "left" },
	{ key: "deposits", label: "Revenue", color: "#e86aa8", axis: "left" },
	{ key: "deposit_sum", label: "Revenue $", color: "#2eb8b8", axis: "left" },
];

const DEFAULT_ACTIVE_METRICS: Record<HourlyMetricKey, boolean> = {
	impressions: false,
	cost: true,
	cpa_fact: true,
	installs: true,
	registrations: false,
	deposits: false,
	deposit_sum: false,
};

const HOURLY_MONEY_KEYS = new Set<HourlyMetricKey>([
	"cost",
	"cpa_fact",
	"deposit_sum",
]);

function formatHourlyTooltipValue(
	value: number | null,
	dataKey: HourlyMetricKey | undefined,
): string {
	if (value == null) return "—";
	if (dataKey && HOURLY_MONEY_KEYS.has(dataKey)) {
		return fmtMoney(value);
	}
	return fmtNum(value);
}

function seedRand(seed: number) {
	const x = Math.sin(seed) * 10000;
	return x - Math.floor(x);
}

/** Real hourly rows, or an even spread of daily totals when the backend has none. */
export function buildHourly(campaign: CampaignCardView): CampaignHourPoint[] {
	if (campaign.hourlyMetrics.length) return campaign.hourlyMetrics;

	const kpisNotApplicable =
		campaign.classification?.kpiStatus === "not_applicable";

	const weights = Array.from({ length: 24 }, (_, h) => {
		const base =
			0.4 + 0.6 * (Math.sin(((h - 6) / 24) * Math.PI * 2) * 0.5 + 0.5);
		const jitter = 0.85 + seedRand(campaign.id * 97 + h) * 0.3;
		return base * jitter;
	});
	const wSum = weights.reduce((a, b) => a + b, 0);
	const m = campaign.metrics;

	return weights.map((w, h) => {
		const k = w / wSum;
		const cost = m.cost * k;
		const installs = Math.round(m.inst * k);
		return {
			hour: `${String(h).padStart(2, "0")}:00`,
			impressions: Math.round(m.impr * k),
			cost: Number(cost.toFixed(2)),
			// no installs or a heartbeat campaign: leave a gap, not a $0 CPA
			cpa_fact:
				!kpisNotApplicable && installs > 0
					? Number((cost / installs).toFixed(2))
					: null,
			installs,
			registrations: Math.round(m.reg * k),
			deposits: Math.round(m.dep * k),
			deposit_sum: Number((m.dep_sum * k).toFixed(2)),
		};
	});
}

const tooltipStyle = {
	backgroundColor: "var(--window-background-color)",
	border: "1px solid var(--window-border-color)",
	borderRadius: 6,
	fontSize: 11,
	color: "var(--main-text-color)",
};

export const HourlySection = ({ campaign }: Props) => {
	const data = useMemo(() => buildHourly(campaign), [campaign]);
	const [active, setActive] = useState(() =>
		readHourlyMetricsSelection(DEFAULT_ACTIVE_METRICS),
	);
	const activeRef = useRef(active);

	useEffect(() => {
		const syncFromStorage = () => {
			const next = readHourlyMetricsSelection(DEFAULT_ACTIVE_METRICS);
			activeRef.current = next;
			setActive(next);
		};
		const onStorage = (event: StorageEvent) => {
			if (event.key === null || event.key === HOURLY_METRICS_STORAGE_KEY) {
				syncFromStorage();
			}
		};
		const onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted) syncFromStorage();
		};
		window.addEventListener("storage", onStorage);
		window.addEventListener("pageshow", onPageShow);
		return () => {
			window.removeEventListener("storage", onStorage);
			window.removeEventListener("pageshow", onPageShow);
		};
	}, []);

	const toggleMetric = (key: HourlyMetricKey) => {
		const current = activeRef.current;
		const next = { ...current, [key]: !current[key] };
		activeRef.current = next;
		saveHourlyMetricsSelection(next);
		setActive(next);
	};

	return (
		<section className='campaign-detail__hourly campaign-detail__panel'>
			<div className='campaign-detail__panel-body'>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 12,
						marginBottom: 12,
					}}>
					<h3 className='campaign-detail__section-title' style={{ margin: 0, border: 0 }}>
						Hourly metrics
					</h3>
					<div className='campaign-detail__hourly-toggles'>
						{HOURLY_METRICS.map((m) => {
							const on = active[m.key];
							return (
								<button
									key={m.key}
									type='button'
									className={`campaign-detail__hourly-toggle${on ? " campaign-detail__hourly-toggle--on" : ""}`}
									onClick={() => toggleMetric(m.key)}>
									<span
										className='campaign-detail__hourly-dot'
										style={{
											background: on
												? m.color
												: "var(--secondary-text-color)",
											opacity: on ? 1 : 0.4,
										}}
									/>
									{m.label}
								</button>
							);
						})}
					</div>
				</div>
				<div className='campaign-detail__chart-height' style={{ height: 288 }}>
					<ResponsiveContainer width='100%' height='100%'>
						<LineChart data={data} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
							<CartesianGrid
								stroke='var(--window-border-color)'
								strokeDasharray='3 3'
							/>
							<XAxis
								dataKey='hour'
								tick={{ fill: "var(--secondary-text-color)", fontSize: 10 }}
							/>
							<YAxis
								yAxisId='left'
								tick={{ fill: "var(--secondary-text-color)", fontSize: 10 }}
							/>
							<YAxis
								yAxisId='right'
								orientation='right'
								tick={{ fill: "var(--secondary-text-color)", fontSize: 10 }}
							/>
							<Tooltip
								contentStyle={tooltipStyle}
								formatter={(value, _name, item) => {
									// null is a missing value; Number(null) would show $0.00
									const raw =
										typeof value === "number"
											? value
											: Number(value ?? NaN);
									const dataKey = item?.dataKey as
										| HourlyMetricKey
										| undefined;
									return [
										formatHourlyTooltipValue(
											Number.isFinite(raw) ? raw : null,
											dataKey,
										),
										item?.name ?? dataKey ?? "",
									];
								}}
							/>
							{HOURLY_METRICS.filter((m) => active[m.key]).map((m) => (
								<Line
									key={m.key}
									yAxisId={m.axis}
									type='monotone'
									dataKey={m.key}
									name={m.label}
									stroke={m.color}
									strokeWidth={2}
									dot={false}
									activeDot={{ r: 4 }}
								/>
							))}
						</LineChart>
					</ResponsiveContainer>
				</div>
			</div>
		</section>
	);
};
